import { create } from "zustand";
import {
  fetchTeams,
  fetchSortedMembersPage,
  type TeamMember,
} from "@/components/main/utils/teams/TeamsApi";
import { fetchModels } from "@/components/main/utils/models/ModelServices";
import { type ModelElementType } from "components/entities";
import { TeamAccess } from "target_wasm";
import { createLogger } from "debug/DebugingLogger";
import { fetchWithInit } from "./fetchWithInit";
import { useMainStateStore } from "./mainStateStore";

const refreshSelectedModelFrom = (models: ModelElementType[]) => {
  const sel = useMainStateStore.getState().selectedModel;
  if (!sel) return;
  const refreshed = models.find((m) => m.model_id === sel.model_id);
  if (refreshed && refreshed !== sel) {
    useMainStateStore.getState().setSelectedModel(refreshed);
  }
};

function mergeStable<T>(
  prev: T[],
  next: T[],
  idOf: (item: T) => string,
  fingerprint: (item: T) => string,
): T[] {
  if (prev.length === 0) return next;
  const prevById = new Map<string, T>();
  for (const item of prev) prevById.set(idOf(item), item);

  let identical = next.length === prev.length;
  const merged: T[] = new Array(next.length);
  for (let i = 0; i < next.length; i++) {
    const item = next[i];
    const cached = prevById.get(idOf(item));
    if (cached && fingerprint(cached) === fingerprint(item)) {
      merged[i] = cached;
      if (identical && prev[i] !== cached) identical = false;
    } else {
      merged[i] = item;
      identical = false;
    }
  }
  return identical ? prev : merged;
}

const teamId = (t: TeamAccess) => t.team_id_str();
const teamFingerprint = (t: TeamAccess) => {
  const d = t.team_details_js();
  return [
    t.team_id_str(),
    t.relation_display_js(),
    d.team_name_js() ?? "",
    d.team_description_js() ?? "",
    d.team_color_js() ?? "",
    d.team_asset_js() ?? "",
    String(d.created_at_js() ?? ""),
  ].join("|");
};

const memberId = (m: TeamMember) => m.user.user_id_js();
const memberFingerprint = (m: TeamMember) =>
  [
    m.user.user_id_js(),
    m.user.user_name_js() ?? "",
    m.user.user_email_js() ?? "",
    m.accessLevel,
    m.isPending ? "1" : "0",
    m.isCurrentUser ? "1" : "0",
  ].join("|");

const modelId = (m: ModelElementType) => m.model_id;
const modelFingerprint = (m: ModelElementType) =>
  [
    m.model_id,
    m.name ?? "",
    m.color ?? "",
    String(m.icon ?? ""),
    m.relation ?? "",
    m.team_access ?? "",
    String(m.last_modified_at ?? ""),
    String(m.last_opened ?? ""),
  ].join("|");

type TeamMembers = Record<string, TeamMember[]>;
type TeamModels = Record<string, ModelElementType[]>;
type TeamDetailsLoading = Record<string, boolean>;

const omitKey = <V>(
  record: Record<string, V>,
  key: string,
): Record<string, V> => {
  if (!(key in record)) return record;
  const { [key]: _omitted, ...rest } = record;
  return rest;
};

export const appendUnique = <T>(
  existing: T[],
  next: T[],
  idOf: (item: T) => string,
): T[] => {
  const seen = new Set(existing.map(idOf));
  const additions = next.filter((item) => !seen.has(idOf(item)));
  return additions.length === 0 ? existing : [...existing, ...additions];
};

const FRESH_TTL_MS = 30_000;
const isFresh = (fetchedAt: number | undefined): boolean =>
  fetchedAt !== undefined && Date.now() - fetchedAt < FRESH_TTL_MS;

const shouldSkipFetch = (
  hasCache: boolean,
  refresh: boolean,
  force: boolean,
  fetchedAt: number | undefined,
): boolean => {
  if (!hasCache) return false;
  if (force) return false;
  if (fetchedAt === undefined) return false;
  if (!refresh) return true;
  return isFresh(fetchedAt);
};

const log = createLogger("useTeamsStore");

interface TeamsState {
  teams: TeamAccess[];
  teamsFetchedAt: number | undefined;
  teamMembers: TeamMembers;
  teamMembersFetchedAt: Record<string, number>;
  teamMembersToken: Record<string, string | null>;
  teamMembersLoadingMore: Record<string, boolean>;
  teamModels: TeamModels;
  teamModelsFetchedAt: Record<string, number>;
  teamDetailsLoading: TeamDetailsLoading;
  selectedTeamId: string | null;
  isLoading: boolean;
  error: string | null;
  setSelectedTeamId: (teamId: string | null) => void;
  addTeam: (team: TeamAccess) => void;
  updateTeam: (team: TeamAccess) => void;
  removeTeam: (teamId: string) => void;
  fetchTeamsList: (options?: {
    showLoading?: boolean;
    refresh?: boolean;
    force?: boolean;
  }) => Promise<TeamAccess[]>;
  fetchTeamMembers: (
    teamId: string,
    refresh?: boolean,
    force?: boolean,
  ) => Promise<void>;
  loadMoreTeamMembers: (teamId: string) => Promise<void>;
  removeTeamMemberFromCache: (teamId: string, memberId: string) => void;
  updateTeamMemberRole: (
    teamId: string,
    memberId: string,
    newAccessLevel: string,
  ) => void;
  fetchTeamModels: (
    teamId: string,
    refresh?: boolean,
    showLoading?: boolean,
    force?: boolean,
  ) => Promise<void>;
  removeModelFromTeam: (teamId: string, modelId: string) => void;
  addModelToTeam: (teamId: string, model: ModelElementType) => void;
  updateModel: (updatedModel: ModelElementType) => void;
  invalidateTeamModels: (teamId?: string) => void;
  invalidateTeamMembers: (teamId?: string) => void;
  invalidateTeams: () => void;
  setError: (message: string | null) => void;
}

export const useTeamsStore = create<TeamsState>((set, get) => ({
  teams: [],
  teamsFetchedAt: undefined,
  teamMembers: {},
  teamMembersFetchedAt: {},
  teamMembersToken: {},
  teamMembersLoadingMore: {},
  teamModels: {},
  teamModelsFetchedAt: {},
  teamDetailsLoading: {},
  selectedTeamId: null,
  isLoading: false,
  error: null,
  setSelectedTeamId: (teamId) => set({ selectedTeamId: teamId }),
  addTeam: (team) => {
    set((state) => ({
      teams: [
        ...state.teams.filter((t) => t.team_id_str() !== team.team_id_str()),
        team,
      ],
    }));
  },
  updateTeam: (team) =>
    set((state) => {
      const teamId = team.team_id_str();
      return {
        teams: state.teams.map((t) => (t.team_id_str() === teamId ? team : t)),
      };
    }),
  removeTeam: (teamId) =>
    set((state) => {
      const next = state.teams.filter((t) => t.team_id_str() !== teamId);
      if (next.length === state.teams.length) return state;
      return {
        teams: next,
        teamMembers: omitKey(state.teamMembers, teamId),
        teamMembersFetchedAt: omitKey(state.teamMembersFetchedAt, teamId),
        teamMembersToken: omitKey(state.teamMembersToken, teamId),
        teamMembersLoadingMore: omitKey(state.teamMembersLoadingMore, teamId),
        teamModels: omitKey(state.teamModels, teamId),
        teamModelsFetchedAt: omitKey(state.teamModelsFetchedAt, teamId),
        teamDetailsLoading: omitKey(state.teamDetailsLoading, teamId),
        selectedTeamId:
          state.selectedTeamId === teamId ? null : state.selectedTeamId,
      };
    }),
  fetchTeamsList: async (options) => {
    if (options?.refresh && !options?.force) {
      const cachedTeams = get().teams;
      if (cachedTeams.length > 0 && isFresh(get().teamsFetchedAt)) {
        return cachedTeams;
      }
    }
    return fetchWithInit(set, options, {
      initFlag: "teamsInitialized",
      dataKey: "teams",
      cachedData: get().teams,
      fetchFn: () => fetchTeams(),
      setData: (fetched) => {
        const current = get().teams;
        const merged = mergeStable(current, fetched, teamId, teamFingerprint);
        const patch: Partial<TeamsState> = { teamsFetchedAt: Date.now() };
        if (merged !== current) patch.teams = merged;
        set(patch);
      },
      errorMessage: "Failed to fetch teams",
    });
  },
  fetchTeamMembers: async (teamId, refresh = false, force = false) => {
    if (!teamId) return;
    const state = get();
    const hasCache = state.teamMembers[teamId] !== undefined;
    if (
      shouldSkipFetch(
        hasCache,
        refresh,
        force,
        state.teamMembersFetchedAt[teamId],
      )
    ) {
      return;
    }
    try {
      const { members, continuationToken } =
        await fetchSortedMembersPage(teamId);
      set((s) => {
        const cached = s.teamMembers[teamId];
        const merged = mergeStable(
          cached ?? [],
          members,
          memberId,
          memberFingerprint,
        );
        const fetchedAt = {
          ...s.teamMembersFetchedAt,
          [teamId]: Date.now(),
        };
        const token = {
          ...s.teamMembersToken,
          [teamId]: continuationToken,
        };
        if (cached !== undefined && merged === cached) {
          return { teamMembersFetchedAt: fetchedAt, teamMembersToken: token };
        }
        return {
          teamMembers: { ...s.teamMembers, [teamId]: merged },
          teamMembersFetchedAt: fetchedAt,
          teamMembersToken: token,
        };
      });
    } catch (error) {
      log.error("Failed to fetch team members", error);
    }
  },
  loadMoreTeamMembers: async (teamId) => {
    if (!teamId) return;
    const state = get();
    const token = state.teamMembersToken[teamId];
    if (!token || state.teamMembersLoadingMore[teamId]) return;
    set((s) => ({
      teamMembersLoadingMore: { ...s.teamMembersLoadingMore, [teamId]: true },
    }));
    try {
      const { members, continuationToken } = await fetchSortedMembersPage(
        teamId,
        { continuationToken: token },
      );
      set((s) => {
        const existing = s.teamMembers[teamId] ?? [];
        const appended = appendUnique(existing, members, memberId);
        return {
          teamMembers: { ...s.teamMembers, [teamId]: appended },
          teamMembersToken: {
            ...s.teamMembersToken,
            [teamId]: continuationToken,
          },
          teamMembersLoadingMore: {
            ...s.teamMembersLoadingMore,
            [teamId]: false,
          },
        };
      });
    } catch (error) {
      log.error("Failed to load more team members", error);
      set((s) => ({
        teamMembersLoadingMore: {
          ...s.teamMembersLoadingMore,
          [teamId]: false,
        },
      }));
    }
  },
  removeTeamMemberFromCache: (teamId, memberId) => {
    set((state) => {
      const existing = state.teamMembers[teamId];
      if (!existing) return state;
      const next = existing.filter((m) => m.user.user_id_js() !== memberId);
      if (next.length === existing.length) return state;
      return {
        teamMembers: { ...state.teamMembers, [teamId]: next },
      };
    });
  },
  updateTeamMemberRole: (teamId, memberId, newAccessLevel) => {
    set((state) => {
      const existing = state.teamMembers[teamId];
      if (!existing) return state;
      return {
        teamMembers: {
          ...state.teamMembers,
          [teamId]: existing.map((m) =>
            m.user.user_id_js() === memberId
              ? { ...m, accessLevel: newAccessLevel }
              : m,
          ),
        },
      };
    });
  },
  fetchTeamModels: async (
    teamId,
    refresh = false,
    showLoading = true,
    force = false,
  ) => {
    if (!teamId) return;
    const state = get();
    const hasCache = state.teamModels[teamId] !== undefined;
    if (
      shouldSkipFetch(
        hasCache,
        refresh,
        force,
        state.teamModelsFetchedAt[teamId],
      )
    ) {
      return;
    }

    if (showLoading) {
      set((s) => ({
        teamDetailsLoading: { ...s.teamDetailsLoading, [teamId]: true },
      }));
    }

    try {
      const models = await fetchModels(teamId);
      let mergedForTeam: ModelElementType[] | undefined;
      set((s) => {
        const cached = s.teamModels[teamId];
        const merged = mergeStable(
          cached ?? [],
          models,
          modelId,
          modelFingerprint,
        );
        mergedForTeam = merged;
        const sameContent = cached !== undefined && merged === cached;
        return {
          ...(sameContent
            ? {}
            : { teamModels: { ...s.teamModels, [teamId]: merged } }),
          teamModelsFetchedAt: {
            ...s.teamModelsFetchedAt,
            [teamId]: Date.now(),
          },
          teamDetailsLoading: { ...s.teamDetailsLoading, [teamId]: false },
        };
      });
      refreshSelectedModelFrom(mergedForTeam ?? []);
    } catch (error) {
      log.error("Failed to fetch team models", error);
      set((s) => ({
        teamDetailsLoading: { ...s.teamDetailsLoading, [teamId]: false },
      }));
    }
  },
  removeModelFromTeam: (teamId, modelId) => {
    set((state) => {
      const teamModels = state.teamModels[teamId];
      if (!teamModels) return state;
      return {
        teamModels: {
          ...state.teamModels,
          [teamId]: teamModels.filter((m) => m.model_id !== modelId),
        },
      };
    });
  },
  addModelToTeam: (teamId, model) => {
    set((state) => {
      const teamModels = state.teamModels[teamId] || [];
      const existingIndex = teamModels.findIndex(
        (m) => m.model_id === model.model_id,
      );
      return {
        teamModels: {
          ...state.teamModels,
          [teamId]:
            existingIndex >= 0
              ? teamModels.map((m) =>
                  m.model_id === model.model_id ? model : m,
                )
              : [...teamModels, model],
        },
      };
    });
  },
  updateModel: (updatedModel) => {
    set((state) => {
      const updatedTeamModels: TeamModels = {};
      let hasUpdates = false;
      for (const [teamId, teamModels] of Object.entries(state.teamModels)) {
        if (teamModels.some((m) => m.model_id === updatedModel.model_id)) {
          updatedTeamModels[teamId] = teamModels.map((m) =>
            m.model_id === updatedModel.model_id ? updatedModel : m,
          );
          hasUpdates = true;
        } else {
          updatedTeamModels[teamId] = teamModels;
        }
      }
      return hasUpdates ? { teamModels: updatedTeamModels } : state;
    });
  },
  invalidateTeamModels: (teamId) =>
    set((state) => ({
      teamModelsFetchedAt: teamId
        ? omitKey(state.teamModelsFetchedAt, teamId)
        : {},
    })),
  invalidateTeamMembers: (teamId) =>
    set((state) => ({
      teamMembersFetchedAt: teamId
        ? omitKey(state.teamMembersFetchedAt, teamId)
        : {},
    })),
  invalidateTeams: () => set({ teamsFetchedAt: undefined }),
  setError: (message) => set({ error: message }),
}));
